import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  checkStatusCodeSuccess,
  notFound,
  rupeeSymbol,
  TaxType,
} from "Components/constants/Common";
import { useFormik } from "formik";
import { submitLabel, updateLabel } from "Components/constants/Role";
import { getFullyPONumberList } from "Api/RepairOrderOpen";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { validationMessages } from "Components/constants/validation";
import * as yup from "yup";
import Loader from "Components/Base/BaseLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getViewPO } from "Api/RoPurchaseOrder";
import { amountCountApi } from "Api/Quotation";
import { QuotationLabel } from "Components/constants/Quotation";
import {
  ADDPOReturn,
  EDITPOReturn,
  VIEWPOReturn,
} from "Components/constants/ReturnRONPO";
import {
  addPOReturn,
  editPOReturn,
  viewPoReturn,
  viewSinglePoReturn,
} from "Api/POReturn";
import {
  AMOUNT_LABEL,
  AT_LEASE_ONE_ROW_HAVE_VALID_RETURN_QTY,
  GST_LABEL,
  ITEM_CODE_LABEL,
  ITEM_DESC_LABEL,
  PLEASE_SELECT_ONE_ROW,
  PO_NO_LABEL,
  QTY_LABEL,
  RATE_LABEL,
  RETURN_QTY_LABEL,
  RETURN_QTY_MUST_BE_GREATER_ZERO,
  SELECT_LABEL,
  SR_NO_LABEL,
  VENDOR_ID_LABEL,
  VENDOR_NAME_LABEL,
} from "Components/constants/ROModule";

interface Vendor {
  id: number;
  value: number;
  label: string;
  user_draft_id: string;
  countAmount: any;
}

const POReturnForm = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [viewPR, setViewPR] = useState<any[]>([]);
  const [selectedRONo, setSelectedRONo] = useState<any | null>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [RONumberList, setRONumberList] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const { id } = useParams<{ id: string }>();
  const validationSchema = yup.object({
    RONumber: yup.string().required(validationMessages.required(PO_NO_LABEL)),
  });
  const [vendorData, setVendorData] = useState<any[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [poDetails, setPODetails] = useState<any>(null);
  const [poid, setPoid] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = location.pathname.includes("viewPOReturn");

  const getPageTitle = () => {
    if (isViewMode) return VIEWPOReturn;
    if (id) return EDITPOReturn;
    return ADDPOReturn;
  };
  const label = id ? updateLabel : submitLabel;

  const fetchRODetailsById = (id: number) => {
    setLoader(true);
    getViewPO(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setPODetails(data);
          setPoid(data.id);
          if (data.repairOrder) {
            setRONumberList([
              {
                label: data.po_no,
                value: data.id,
              },
            ]);

            setSelectedRONo(data.id);

            setTimeout(() => {
              formik.setFieldValue("PONumber", data.po_no);
            }, 0);
          }

          if (data.vendor) {
            setSelectedVendor({
              id: data.vendor.id,
              value: data.vendor.id,
              label: data.vendor.name,
              user_draft_id: data.user_draft_id,
              countAmount: 0,
            });
          }

          const transformedItems = data.repairOrderPurchaseOrderItem.map(
            (item: any) => ({
              ...item,
              item_code: item.product?.product_code,
              parts_name: item.product?.product_description,
              uom: item.product?.uom,
              stock: item.product?.quantity,
              qty: item?.request_quantity,
              material_type: item.product?.material?.name,
              material_group: item.product?.materialGroup?.name,
              material_group_description:
                item.product?.material_group_description,
              store_location: item.product?.store?.store_name,
              rack_location: item.product?.rake_no,
              vendor_id: data.vendor_id,
              vendor_name: data.vendor?.name,
              user_draft_id: data.user_draft_id,
              return_quantity: 0,
              amount: 0,
            })
          );

          setViewPR(transformedItems);

          const total = transformedItems.reduce(
            (sum: number, item: any) => sum + (item.amount || 0),
            0
          );
          setTotalAmount(total);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchPODetailsById = (id: any) => {
    setLoader(true);
    isViewMode
      ? viewSinglePoReturn(id)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              const data = res?.data;
              setPoid(data[0]?.po_id);
              setRONumberList([
                {
                  label: data[0]?.po_no,
                  value: data[0]?.po_id,
                },
              ]);

              setSelectedRONo(data[0]?.po_id);
              formik.setFieldValue("RONumber", data[0]?.po_id);

              const transformedItems = data.map((item: any) => ({
                ...item,
                item_code: item.item_code,
                parts_name: item.item_description,
                vendor_name: item.vendor_name,
                user_draft_id: item.user_draft_id,
                required_quantity: item.Quantity,
                return_quantity: item.return_quantity,
                rate: item.rate,
                gst: item.gst,
                amount: item.amount,
              }));

              setViewPR(transformedItems);

              const total = transformedItems.reduce(
                (sum: number, item: any) => sum + (item.amount || 0),
                0
              );
              setTotalAmount(total);
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setLoader(false);
          })
      : viewPoReturn(id)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              const data = res?.data;
              setPODetails(data.ro_po_id);
              setPoid(data.id);

              setRONumberList([
                {
                  label: data.po_no,
                  value: data.id,
                },
              ]);

              setSelectedRONo(data.id);
              formik.setFieldValue("RONumber", data.id);

              if (data.vendor) {
                setSelectedVendor({
                  id: data.vendor.id,
                  value: data.vendor.id,
                  label: data.vendor.name,
                  user_draft_id: data.user_draft_id,
                  countAmount: 0,
                });
              }

              const transformedItems = data.repairOrderPurchaseOrderItem.map(
                (item: any) => ({
                  ...item,
                  item_code: item.product?.product_code,
                  parts_name: item.product?.product_description,
                  uom: item.product?.uom,
                  stock: item.product?.quantity,
                  qty: item?.request_quantity,
                  material_type: item.product?.material?.name,
                  material_group: item.product?.materialGroup?.name,
                  material_group_description:
                    item.product?.material_group_description,
                  store_location: item.product?.store?.store_name,
                  rack_location: item.product?.rake_no,
                  vendor_id: data.vendor_id,
                  vendor_name: data.vendor?.name,
                  user_draft_id: data.user_draft_id,
                })
              );

              setViewPR(transformedItems);

              const total = transformedItems.reduce(
                (sum: number, item: any) => sum + (item.amount || 0),
                0
              );
              setTotalAmount(total);
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setLoader(false);
          });
  };

  useEffect(() => {
    if (id) {
      fetchPODetailsById(id);
    } else {
      setViewPR([]);
      getFullyPONumberList()
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const data = res?.data;
            setRONumberList(
              data.map((item: any) => ({
                label: item.po_no,
                value: item.id,
              }))
            );
          } else {
            toast.error(res.message);
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
        });
    }
  }, [id]);

  const countAmount = (data: any, rowIndex: number) => {
    const payload = {
      quantity: data.return_quantity,
      gst: data.gst,
      rate: data.rate,
    };

    const isValidPayload =
      payload.quantity !== null &&
      payload.gst !== null &&
      payload.rate !== null;

    if (isValidPayload) {
      setLoader(true);

      amountCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;

            setViewPR((prevData) => {
              const updatedData = [...prevData];
              updatedData[rowIndex] = {
                ...updatedData[rowIndex],
                amount: updatedAmount,
              };
              return updatedData;
            });
            if (gridRef.current?.api) {
              const rowNode =
                gridRef.current.api.getDisplayedRowAtIndex(rowIndex);
              if (rowNode) {
                gridRef.current.api.refreshCells({
                  rowNodes: [rowNode],
                  columns: ["amount"],
                });
              }
            }
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const PartRequisitionColumns = useMemo(
    () => [
      {
        headerName: SELECT_LABEL,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 150,
        flex: 1.5,
      },
      {
        headerName: SR_NO_LABEL,
        field: "srNo",
        valueGetter: "node.rowIndex + 1",
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: VENDOR_NAME_LABEL,
        field: "vendor_name",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: vendorData.map((vendor) => vendor.name),
        },
        onCellValueChanged: (event: any) => {
          const { newValue, node } = event;
          const selectedVendor = vendorData.find(
            (vendor) => vendor.name === newValue
          );
          if (selectedVendor) {
            setViewPR((prevData) => {
              const updatedData = [...prevData];
              updatedData[node.rowIndex] = {
                ...updatedData[node.rowIndex],
                vendor_id: selectedVendor.id,
                vendor_name: selectedVendor.name,
                user_draft_id: selectedVendor.user_draft_id,
              };
              return updatedData;
            });
            setSelectedVendor(selectedVendor);
          }
        },
      },
      {
        headerName: VENDOR_ID_LABEL,
        field: "user_draft_id",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: ITEM_CODE_LABEL,
        field: "item_code",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: ITEM_DESC_LABEL,
        field: "parts_name",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: QTY_LABEL,
        field: "required_quantity",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: RETURN_QTY_LABEL,
        field: "return_quantity",
        headerClass: "required",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
        valueSetter: (params: any) => {
          const newValue = parseFloat(params.newValue);
          if (!isNaN(newValue)) {
            params.data.return_quantity = newValue;
            countAmount(params.data, params.node.rowIndex);
            return true;
          }
          return false;
        },
      },
      {
        headerName: RATE_LABEL,
        field: "rate",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
        valueSetter: (params: any) => {
          const newValue = parseFloat(params.newValue);
          if (!isNaN(newValue)) {
            params.data.rate = newValue;
            countAmount(params.data, params.node.rowIndex);
            return true;
          }
          return false;
        },
      },
      {
        headerName: GST_LABEL,
        field: "gst",
        editable: false,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: TaxType?.map((option) => option?.label),
        },
        valueSetter: (params: any) => {
          const newValue = params.newValue;
          const selectedOption = TaxType?.find(
            (opt) => opt?.label === newValue
          );
          if (selectedOption) {
            params.data.gst = selectedOption.value;
            countAmount(params.data, params.node.rowIndex);
            return true;
          }
          return false;
        },
        valueGetter: (params: any) => {
          const selectedOption = TaxType?.find(
            (opt) => opt?.value === params.data.gst
          );
          return selectedOption ? selectedOption.label : "";
        },
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: AMOUNT_LABEL,
        field: "amount",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params.value != null) {
            return rupeeSymbol(params.value.toFixed(2));
          }
          return rupeeSymbol("0.00");
        },
      },
    ],
    [vendorData, isViewMode]
  );

  const handleSelectionChanged = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    const total = selectedRows.reduce(
      (sum: number, row: any) => sum + (row.amount || 0),
      0
    );
    setTotalAmount(total);
  };

  const handleSubmit = async (values: any) => {
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];

    if (selectedRows.length === 0) {
      toast.error(PLEASE_SELECT_ONE_ROW);
      return;
    }

    const zeroQtyRow = selectedRows.some(
      (row) =>
        row.return_quantity === 0 ||
        row.return_quantity === undefined ||
        row.return_quantity === null
    );
    if (zeroQtyRow) {
      toast.error(RETURN_QTY_MUST_BE_GREATER_ZERO);
      return;
    }

    if (!selectedRows.some((row) => row.return_quantity > 0)) {
      toast.error(AT_LEASE_ONE_ROW_HAVE_VALID_RETURN_QTY);
      return;
    }

    const poReturnDetails = selectedRows.map((row) => ({
      id: row.id || "",
      vendor_name: row.vendor_name || "",
      vendor_id: row.vendor_id || "",
      item_code: row.item_code || "",
      item_description: row.parts_name || "",
      Quantity: row.required_quantity || 0,
      return_quantity: row.return_quantity || 0,
      rate: row.rate || 0,
      gst: row.gst || 0,
      amount: row.amount || 0,
    }));

    const payload = {
      po_id: poid || "",
      poReturnDetails: poReturnDetails,
    };

    try {
      setBtnLoader(true);
      if (id) {
        const response = await editPOReturn(Number(id), payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response.message);
          navigate("/POReturn");
        } else {
          toast.error(response.message);
        }
      } else {
        const response = await addPOReturn(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response.message);
          fetchRODetailsById(selectedRONo);
          navigate("/POReturn");
        } else {
          toast.error(response.message);
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setBtnLoader(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      RONumber: "",
      qty: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleRONumberChange = (value: any) => {
    setSelectedRONo(value);
    setViewPR([]);
    if (value) {
      fetchRODetailsById(value);
    } else {
      setViewPR([]);
    }
  };

  document.title = getPageTitle();

  return (
    <div
      className="page-content"
      style={{ cursor: isViewMode ? "not-allowed" : "auto" }}
    >
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{getPageTitle()}</span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/POReturn")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12 mb-4">
                  <BaseSelect
                    name="RONumber"
                    label={PO_NO_LABEL}
                    options={RONumberList}
                    value={formik.values.RONumber}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      handleRONumberChange(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.RONumber}
                    touched={formik.touched.RONumber}
                    required
                    disabled={!!id}
                  />
                </div>
              </div>

              <div className="table-css">
                <div className="ag-theme-quartz">
                  <AgGridReact
                    ref={gridRef}
                    rowData={viewPR}
                    columnDefs={PartRequisitionColumns}
                    suppressRowClickSelection={true}
                    rowSelection="multiple"
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      flex: 1,
                      tooltipComponentParams: {
                        color: "blue",
                      },
                    }}
                    domLayout="normal"
                    getRowHeight={() => 35}
                    overlayNoRowsTemplate={notFound.dataNotFound}
                    onSelectionChanged={handleSelectionChanged}
                  />
                </div>
                <div className="d-flex justify-content-end align-items-start">
                  <div className="mt-4 d-flex gap-4">
                    <div className=" flex-column text-start">
                      <h6 className="mb-1 font-bold text-dark">
                        {QuotationLabel.TotalGSTAmount}
                      </h6>
                      <span className="text-center">
                        {rupeeSymbol(totalAmount.toFixed(2))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {!isViewMode && (
                <div className="d-flex justify-content-end mb-1 mt-4 w-100">
                  <div className="col-12 d-flex justify-content-end mb-0">
                    <BaseButton
                      type="submit"
                      label={label}
                      color="success"
                      loader={btnLoader}
                      disable={btnLoader}
                    />
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POReturnForm;
